import { ImageOff, Play } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ASSET_LIBRARY_API_BASE } from '@/api/assetLibraryClient';

import type { AssetResponse } from '../../api';

/**
 * An OLA banner on a grid tile: the fallback still, and the live ad on hover.
 *
 * The still is what an ad server shows when JavaScript is unavailable, so it is
 * the honest thumbnail — and it is a plain `<img>`, so a grid of fifty banners
 * costs a grid of fifty images rather than fifty iframes each pulling GSAP.
 */

/** Where the token-in-path proxy is mounted. Shares the client's prefix. */
export const OLA_BASE_PATH = `${ASSET_LIBRARY_API_BASE}/ola`;

/**
 * How long the pointer must rest before the ad mounts.
 *
 * Sweeping across a row of twelve cards on the way to something else would
 * otherwise mount and tear down twelve iframes, each starting a GSAP timeline
 * and a dozen requests. Long enough to mean intent, short enough not to feel
 * like a delay. Was 150; the banner's own boot is ~0.6 s after the proxy
 * started rushing Enabler (see `src.core.ola_preview` in the backend), so
 * the debounce became a visible share of the wait.
 */
const HOVER_DELAY_MS = 100;

/** Never render the pop-out larger than this share of the viewport. */
const MAX_VIEWPORT_SHARE = 0.8;

/** Fallback when an ad carries no dimensions — the commonest IAB size. */
const FALLBACK_SIZE = { width: 300, height: 250 };

/** Gap between the pop-out and the viewport edge it was pushed off. */
const EDGE_MARGIN = 8;

interface OlaPreviewProps {
  asset: AssetResponse;
  /** Signed URL for the fallback still — `storage_path` points at it. */
  posterUrl: string | undefined;
  /** Capability token for this banner. Absent means poster only, no hover. */
  token: string | undefined;
  isUrlLoading: boolean;
  onPosterError: () => void;
  onPosterLoad: () => void;
}

interface Placement {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Put the pop-out over the card, scaled down only as far as the viewport needs.
 *
 * Centred on the card and then pushed back inside the viewport rather than
 * flipped to a side: a banner is much larger than its tile, so there is rarely
 * a side it fits on, and nudging keeps it anchored to what the pointer is on.
 */
function placeOver(anchor: DOMRect, width: number, height: number): Placement {
  const scale = Math.min(
    1,
    (window.innerWidth * MAX_VIEWPORT_SHARE) / width,
    (window.innerHeight * MAX_VIEWPORT_SHARE) / height,
  );
  const scaledWidth = Math.round(width * scale);
  const scaledHeight = Math.round(height * scale);

  const left = anchor.left + anchor.width / 2 - scaledWidth / 2;
  const top = anchor.top + anchor.height / 2 - scaledHeight / 2;

  return {
    width: scaledWidth,
    height: scaledHeight,
    left: Math.min(Math.max(EDGE_MARGIN, left), window.innerWidth - scaledWidth - EDGE_MARGIN),
    top: Math.min(Math.max(EDGE_MARGIN, top), window.innerHeight - scaledHeight - EDGE_MARGIN),
  };
}

export function OlaPreview({
  asset,
  posterUrl,
  token,
  isUrlLoading,
  onPosterError,
  onPosterLoad,
}: OlaPreviewProps) {
  const tileRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);
  const hovered = useRef(false);
  const [placement, setPlacement] = useState<Placement | null>(null);

  const canPlay = token !== undefined && asset.ola_entry_path !== null;
  // Read at fire time, not at arm time: the token query can resolve during
  // the debounce, and a hover that started a moment too early should still
  // play rather than being silently ignored until the next re-enter.
  const canPlayRef = useRef(canPlay);
  canPlayRef.current = canPlay;

  const stop = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    setPlacement(null);
  }, []);

  // Unmounting mid-hover would otherwise leave the timer to fire against a
  // component that no longer exists — and a scrolled-away grid must not pop
  // an ad open over whatever replaced it.
  useEffect(() => stop, [stop]);

  const arm = useCallback(() => {
    if (timer.current !== null) return;
    timer.current = window.setTimeout(() => {
      timer.current = null;
      if (!canPlayRef.current) return;
      const anchor = tileRef.current?.getBoundingClientRect();
      if (!anchor) return;
      setPlacement(
        placeOver(anchor, asset.width ?? FALLBACK_SIZE.width, asset.height ?? FALLBACK_SIZE.height),
      );
    }, HOVER_DELAY_MS);
  }, [asset.height, asset.width]);

  const handleEnter = useCallback(() => {
    hovered.current = true;
    arm();
  }, [arm]);

  const handleLeave = useCallback(() => {
    hovered.current = false;
    stop();
  }, [stop]);

  // The token landed while the pointer was already resting on the tile and
  // the timer had fired into nothing: arm again, as if the hover began now.
  useEffect(() => {
    if (canPlay && hovered.current && placement === null) arm();
  }, [arm, canPlay, placement]);

  return (
    <div
      ref={tileRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="relative flex h-full w-full items-center justify-center"
    >
      {posterUrl ? (
        <img
          src={posterUrl}
          alt={asset.filename_original}
          loading="lazy"
          onError={onPosterError}
          onLoad={onPosterLoad}
          className="max-h-full max-w-full object-contain"
        />
      ) : isUrlLoading ? (
        <div className="h-full w-full animate-pulse rounded bg-slate-200" />
      ) : (
        <div className="flex flex-col items-center gap-1 text-slate-400">
          <ImageOff className="h-5 w-5" strokeWidth={1.5} />
          <span className="text-[11px]">Preview unavailable</span>
        </div>
      )}

      {/* Says the tile is animated even before anyone hovers it — a banner and
          a JPEG are otherwise indistinguishable at a glance. */}
      <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white shadow-sm backdrop-blur-[2px]">
        <Play className="h-3 w-3" strokeWidth={2} />
        HTML5
      </div>

      {placement !== null &&
        token !== undefined &&
        createPortal(
          // Portalled to `body` for the reason the detail overlay is: the page
          // renders inside `<main class="relative z-[1]">`, a stacking context,
          // so no z-index in here can paint over the app's sidebar.
          <div
            style={{
              left: placement.left,
              top: placement.top,
              width: placement.width,
              height: placement.height,
            }}
            // `pointer-events-none` so the pop-out cannot steal the pointer
            // from the card underneath: the mouseleave that tears it down is on
            // the tile, and a frame that swallows the pointer never lets it fire.
            className="pointer-events-none fixed z-[80] overflow-hidden rounded-lg border border-slate-300 bg-white shadow-2xl"
          >
            {/* No poster in here, on purpose. The iframe's `load` fires before
                the banner paints its first frame, so poster-then-hide produced
                poster, half a second of blank, then motion — worse than a
                plain box that the animation simply appears in. The tile
                underneath keeps showing the poster throughout. */}
            <iframe
              src={`${OLA_BASE_PATH}/${token}/${asset.ola_entry_path}`}
              title={asset.filename_original}
              width={placement.width}
              height={placement.height}
              // Never `allow-same-origin`: the frame could then read the Okta
              // token out of localStorage. The backend's CSP pins the same
              // sandbox — see `apps/asset-library/backend/src/api/ola_serve.py`.
              sandbox="allow-scripts"
              referrerPolicy="no-referrer"
              scrolling="no"
              className="block border-0"
            />
          </div>,
          document.body,
        )}
    </div>
  );
}
